import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import env from '@config/env';
import { useOrganization } from '@context/OrganizationContext';
import { SubscriptionCartFooter } from '@features/subscriptions/components/subscription-cart-footer';
import { useGetActiveSubscriptions } from '@features/subscriptions/hooks/useGetActiveSubscriptions';
import { useGetSaaSProducts } from '@features/subscriptions/hooks/useGetSaaSProducts';
import { Loading } from '@goodfynd/ui';
import { useLocalStorageState } from '@hooks/useLocalStorageState';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type {
  SaasProduct,
  SubscriptionCart,
  SubscriptionFrequency,
  SubscriptionLineItem,
} from 'types/account/subscriptions';

import type {
  SubscriptionsContextState,
  SubscriptionsContextValue,
  SubscriptionsDispatchContextValue,
  SubscriptionsProviderProps,
} from './types';

import classes from './SubscriptionsContext.module.css';

const stripePromise = loadStripe(env.STRIPE_PUBLISHABLE_KEY_SAAS);

// Map addon keys to their matching product SKUs
const addonSkuMap: Record<string, string> = {
  events: 'addon-leads-food-trucks',
  'leads-catering': 'addon-leads-catering',
  'leads-food-trucks': 'addon-leads-food-trucks',
  invoice: 'addon-invoice',
  kds: 'addon-kds',
  kiosk: 'addon-kiosk',
  website: 'addon-website',
  'online-ordering': 'addon-online-ordering',
  pos: 'addon-pos',
};

export const SubscriptionsContext = createContext<
  SubscriptionsContextValue | undefined
>(undefined);

export const SubscriptionsDispatchContext = createContext<
  SubscriptionsDispatchContextValue | undefined
>(undefined);

export const SubscriptionsProvider = function SubscriptionsProvider({
  children,
  initialAddons = [],
}: SubscriptionsProviderProps) {
  const { organization } = useOrganization();

  const initialAddonSkus = useMemo(() => {
    return initialAddons.map((addon) => addonSkuMap[addon]);
  }, [initialAddons]);

  const [
    savedCart = {
      frequency: 'monthly',
      items: [],
      paymentMethodId: '',
    },
    setSavedCart,
  ] = useLocalStorageState<SubscriptionCart>(
    `subscription-cart-${organization.id}`,
    {
      frequency: 'monthly',
      items: [],
      paymentMethodId: '',
    }
  );
  const [state, setState] = useState<SubscriptionsContextState>({
    cart: savedCart,
  });

  useEffect(() => {
    setSavedCart(state.cart.items[0] ? state.cart : undefined);
  }, [state.cart, setSavedCart]);

  const {
    activeSubscriptions,
    defaultPaymentMethod,
    invalidate: invalidateActiveSubscriptions,
    isFetching: isFetchingActiveSubscriptions,
  } = useGetActiveSubscriptions(organization.id, {
    enabled: !!organization.stripeSaasCustomerId,
    onSuccess(data) {
      if (!data.subscriptions[0]) {
        return;
      }

      setFrequency(data.subscriptions[0].frequency);

      const subscriptionSkus = data.subscriptions.flatMap((subscription) =>
        subscription.items.map((item) => item.price.sku)
      );
      state.cart.items.forEach((item) => {
        if (
          !subscriptionSkus.includes(item.sku) &&
          !initialAddonSkus.includes(item.sku)
        ) {
          removeCartItem(item.sku);
        }
      });

      data.subscriptions.forEach((subscription) => {
        subscription.items.forEach((item) => {
          const cartItem = state.cart.items.find(
            (cartItem) => cartItem.sku === item.price.sku
          );

          if (item.product.categories?.includes('suite')) {
            return;
          }

          if (!cartItem) {
            addCartItem({
              priceId: item.price.stripeId,
              quantity: item.quantity,
              sku: item.price.sku,
              newQuantity: 0,
              previousQuantity: item.quantity,
            });
            return;
          }

          if (cartItem.previousQuantity === item.quantity) {
            return;
          }

          if (item.product.allowMultiple) {
            updateCartItem({
              ...cartItem,
              newQuantity: 0,
              previousQuantity: item.quantity,
            });
          } else {
            updateCartItem({
              ...cartItem,
              newQuantity:
                item.quantity -
                (cartItem.previousQuantity ?? cartItem.quantity),
              previousQuantity: cartItem.previousQuantity ?? cartItem.quantity,
            });
          }
        });
      });
    },
  });
  const {
    activeSuite,
    allProducts,
    invalidate: invalidateSubscriptions,
    isFetching: isFetchingSubscriptions,
    serviceProducts,
    suites,
  } = useGetSaaSProducts(activeSubscriptions, {
    onSuccess(data) {
      data.forEach((item) => {
        if (item.categories?.includes('suite')) {
          return;
        }

        const price = item.prices.find(
          (price) => price.frequency === state.cart.frequency
        );
        if (!price) {
          return;
        }

        const cartItem = state.cart.items.find(
          (cartItem) => cartItem.sku === item.sku
        );

        if (initialAddonSkus.includes(item.sku) && !cartItem) {
          addCartItem({
            priceId: price.stripeId,
            quantity: 1,
            sku: item.sku,
            newQuantity: 0,
          });
          return;
        }
      });
    },
  });

  const findBySku = useCallback(
    (sku: string) => {
      return allProducts.find((item) => item.sku === sku);
    },
    [allProducts]
  );

  const cartSubtotal = useMemo(() => {
    const prices = state.cart.items.map((item) => {
      const subscription = findBySku(item.sku);
      const price =
        subscription?.prices.find(
          (price) => price.frequency === state.cart.frequency
        )?.price || 0;
      const oldTotal = price * (item.previousQuantity ?? 0);
      const newTotal = price * item.quantity;
      return item.previousQuantity !== item.quantity ? newTotal - oldTotal : 0;
    });

    return prices.reduce((acc, price) => acc + price, 0);
  }, [findBySku, state.cart.frequency, state.cart.items]);

  const totalItems = useMemo(() => {
    return state.cart.items.reduce(
      (acc, item) =>
        acc + (item.previousQuantity === item.quantity ? 0 : item.quantity),
      0
    );
  }, [state.cart.items]);

  const activeSubscriptionsTotal = useMemo(() => {
    return activeSubscriptions.reduce(
      (acc, subscription) =>
        acc +
        (subscription.items.reduce(
          (acc, item) => acc + item.price.price * item.quantity,
          0
        ) ?? 0),
      0
    );
  }, [activeSubscriptions]);

  const value = useMemo<SubscriptionsContextValue>(
    () => ({
      ...state,
      activeSubscriptions,
      activeSubscriptionsTotal,
      activeSuite,
      cartSubtotal,
      defaultPaymentMethod,
      hasActiveSubscriptions: !!activeSubscriptions[0],
      hasUpdates:
        totalItems > 0 ||
        state.cart.items.some(
          (item) =>
            item.previousQuantity && item.previousQuantity !== item.quantity
        ),
      products: serviceProducts,
      subscriptionItems: activeSubscriptions.flatMap(
        (subscription) => subscription.items
      ),
      suites,
      totalItems,
    }),
    [
      activeSubscriptions,
      activeSubscriptionsTotal,
      activeSuite,
      cartSubtotal,
      defaultPaymentMethod,
      serviceProducts,
      state,
      suites,
      totalItems,
    ]
  );

  const changeSuite = useCallback(
    (suite?: SaasProduct) => {
      const priceId = suite?.prices.find(
        (price) => price.frequency === state.cart.frequency
      )?.stripeId;
      const newSuite: SubscriptionLineItem | undefined = priceId
        ? {
            priceId,
            quantity: 1,
            newQuantity: 1,
            sku: suite.sku,
          }
        : undefined;
      setState((prevState) => ({
        ...prevState,
        newSuite,
      }));

      return newSuite;
    },
    [state.cart.frequency]
  );

  const clearCart = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      cart: { ...prevState.cart, items: [] },
    }));
  }, []);

  const removeCartItem = useCallback((sku: string) => {
    setState((prevState) => ({
      ...prevState,
      cart: {
        ...prevState.cart,
        items: prevState.cart.items.filter((item) => item.sku !== sku),
      },
    }));
  }, []);

  const resetSubscriptions = useCallback(() => {
    invalidateActiveSubscriptions();
    invalidateSubscriptions();
  }, [invalidateActiveSubscriptions, invalidateSubscriptions]);

  const setFrequency = useCallback((frequency: SubscriptionFrequency) => {
    setState((prevState) => ({
      ...prevState,
      cart: { ...prevState.cart, frequency },
    }));
  }, []);

  const setPaymentMethod = useCallback(
    (paymentMethodId: string, isDefault?: boolean) => {
      setState((prevState) => ({
        ...prevState,
        cart: {
          ...prevState.cart,
          paymentMethodId,
          paymentMethodIsDefault: isDefault,
        },
      }));
    },
    []
  );

  const updateCartItem = useCallback((item: SubscriptionLineItem) => {
    setState((prevState) => ({
      ...prevState,
      cart: {
        ...prevState.cart,
        items: prevState.cart.items.map(({ sku, ...rest }) =>
          sku === item.sku ? item : { sku, ...rest }
        ),
      },
    }));
  }, []);

  const addCartItem = useCallback(
    (item: SubscriptionLineItem) => {
      const cartItem = state.cart.items.find(
        (cartItem) => cartItem.sku === item.sku
      );
      if (cartItem) {
        updateCartItem({
          priceId: item.priceId,
          sku: item.sku,
          quantity: item.quantity,
          newQuantity: item.quantity - (item.previousQuantity ?? 0),
          previousQuantity: item.previousQuantity,
        });
        return;
      }

      setState((prevState) => ({
        ...prevState,
        cart: {
          ...prevState.cart,
          items: [...prevState.cart.items, item],
        },
      }));
    },
    [state.cart.items, updateCartItem]
  );

  const renderContent = useCallback(() => {
    if (isFetchingActiveSubscriptions || isFetchingSubscriptions) {
      return <Loading />;
    }

    return (
      <div className={classes.container}>
        {children}
        <SubscriptionCartFooter />
      </div>
    );
  }, [children, isFetchingActiveSubscriptions, isFetchingSubscriptions]);

  return (
    <SubscriptionsContext.Provider value={value}>
      <SubscriptionsDispatchContext.Provider
        value={{
          addCartItem,
          changeSuite,
          clearCart,
          findBySku,
          removeCartItem,
          resetSubscriptions,
          setFrequency,
          setPaymentMethod,
          updateCartItem,
        }}
      >
        <Elements stripe={stripePromise}>{renderContent()}</Elements>
      </SubscriptionsDispatchContext.Provider>
    </SubscriptionsContext.Provider>
  );
};
